import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, switchMap } from "rxjs";

import { environment } from "../../../../environments/environment";
import { TagCategoryKey } from "../../models/freeform-tags.model";
import {
    AudioFile,
    FreeformSubmissionState,
    HostAudioUploadResponse,
    SubmissionFreeformBulkMoveRequest,
    SubmissionFreeformBulkUpdateResponse,
    SubmissionFreeformHostResponseRequest,
    SubmissionFreeformResponse,
    SubmissionFreeformSearchRequest,
    SubmissionFreeformSearchResponse,
    SubmissionFreeformUpdateCleanedTextAndTagsRequest
} from "../models/submission-freeform.model";

const TAG_CATEGORY_PARAM_KEYS: Record<TagCategoryKey, string> = {
    tagCategoryA: "tag_category_a",
    tagCategoryB: "tag_category_b",
    tagCategoryC: "tag_category_c",
    tagCategoryD: "tag_category_d",
    tagCategoryE: "tag_category_e",
    tagCategoryF: "tag_category_f"
};

@Injectable({ providedIn: "root" })
export class SubmissionFreeformApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = `${environment.interactiveExperienceServiceUrl}/v1/submissions/freeform`;

    searchSubmissions(request: SubmissionFreeformSearchRequest): Observable<SubmissionFreeformSearchResponse> {
        const {
            interactiveIds,
            tags,
            page,
            size,
            sortColumn,
            sortDirection,
            keyword,
            isVoice,
            isText,
            isVideo,
            isSpam,
            minimumScore,
            isFavorite,
            isUsed,
            isPublic,
            hasResponse,
            hasPhoneNumber,
            profileId,
            deviceId,
            segmentId,
            state
        } = request;

        let params = new HttpParams();

        interactiveIds.forEach(id => (params = params.append("interactiveIds", id)));

        params = this.setIfDefined(params, "page", page === undefined ? undefined : page + 1); // Backend page is 1-based
        params = this.setIfDefined(params, "size", size);
        params = this.setIfDefined(params, "sortColumn", sortColumn);
        params = this.setIfDefined(params, "sortDirection", sortDirection);
        params = this.setIfDefined(params, "keyword", keyword);
        params = this.setIfDefined(params, "isVoice", isVoice);
        params = this.setIfDefined(params, "isText", isText);
        params = this.setIfDefined(params, "isVideo", isVideo);
        params = this.setIfDefined(params, "isSpam", isSpam);
        params = this.setIfDefined(params, "minimumScore", minimumScore);
        params = this.setIfDefined(params, "isFavorite", isFavorite);
        params = this.setIfDefined(params, "isUsed", isUsed);
        params = this.setIfDefined(params, "isPublic", isPublic);
        params = this.setIfDefined(params, "hasResponse", hasResponse);
        params = this.setIfDefined(params, "hasPhoneNumber", hasPhoneNumber);
        params = this.setIfDefined(params, "profileId", profileId);
        params = this.setIfDefined(params, "deviceId", deviceId);
        params = this.setIfDefined(params, "segmentId", segmentId);
        params = this.setIfDefined(params, "state", state);

        if (tags) {
            Object.entries(tags).forEach(([categoryKey, values]) => {
                if (!values?.length) return;
                const paramKey = `tags[${TAG_CATEGORY_PARAM_KEYS[categoryKey as TagCategoryKey]}]`;
                values.forEach(v => (params = params.append(paramKey, v)));
            });
        }

        return this.http.get<SubmissionFreeformSearchResponse>(`${this.baseUrl}/search`, {
            params
        });
    }

    private setIfDefined(params: HttpParams, key: string, value: string | number | boolean | undefined): HttpParams {
        return value === undefined ? params : params.set(key, value);
    }

    getPrioritized(interactiveId: string): Observable<SubmissionFreeformResponse[]> {
        return this.http.get<SubmissionFreeformResponse[]>(`${this.baseUrl}/prioritized`, {
            params: { interactiveId }
        });
    }

    updatePriority(id: string, priority: number): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, {
            priority
        });
    }

    updateIsFavorite(id: string, favorite: boolean): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, {
            isFavorite: favorite
        });
    }

    updateIsPublic(id: string, isPublic: boolean): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, {
            isPublic,
            ...(isPublic ? {} : { isFeatured: false }) // A submission can't be featured if it isn't public
        });
    }

    updateIsFeatured(id: string, isFeatured: boolean): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, {
            isFeatured
        });
    }

    updateDisplaySettings(id: string, isPublic: boolean, isFeatured: boolean): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, {
            isPublic,
            isFeatured: isPublic && isFeatured
        });
    }

    updateIsUsed(id: string, isUsed: boolean): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, {
            isUsed
        });
    }

    removePriority(id: string): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, {
            priority: null
        });
    }

    // Highlighting has its own endpoint rather than going through the generic PATCH because
    // only one submission per interactive can be highlighted: the service clears whichever
    // one was highlighted before, which a single-field update couldn't express.
    updateIsHighlighted(id: string, isHighlighted: boolean): Observable<SubmissionFreeformResponse> {
        return this.http.post<SubmissionFreeformResponse>(`${this.baseUrl}/${id}/highlight`, null, {
            params: { highlighted: isHighlighted }
        });
    }

    updateIsSpam(id: string, isSpam: boolean | null): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, {
            isSpam
        });
    }

    updateState(id: string, state: FreeformSubmissionState) {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, {
            state
        });
    }

    updateCleanedTextAndTags(
        id: string,
        request: SubmissionFreeformUpdateCleanedTextAndTagsRequest
    ): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, request);
    }

    updateSegment(id: string, segmentId: string | null): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, { segmentId });
    }

    updateNotes(id: string, notes: string | null): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}`, { notes });
    }

    updateResponse(id: string, request: SubmissionFreeformHostResponseRequest): Observable<SubmissionFreeformResponse> {
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}/host-response`, request);
    }

    uploadResponseAudio(id: string, file: File): Observable<HostAudioUploadResponse> {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<HostAudioUploadResponse>(`${this.baseUrl}/${id}/host-response-audio`, formData);
    }

    getResponseAudioFile(id: string): Observable<AudioFile> {
        return this.fetchAudioFile(`${this.baseUrl}/${id}/host-response-audio`);
    }

    // Assigning a badge from the host view is a full override of whatever badge/visibility
    // the submission already had (e.g. set from the producer dashboard), so this always
    // un-hides the image along with setting the rating.
    updateHostResponse(id: string, responseRating: string | null): Observable<SubmissionFreeformResponse> {
        const request: SubmissionFreeformHostResponseRequest = { responseRating, responseHideBadgeImage: false };
        return this.http.patch<SubmissionFreeformResponse>(`${this.baseUrl}/${id}/host-response`, request);
    }

    // Also handles a single submission - the backend's bulk-move endpoint accepts a one-element
    // submissionIds array just fine, so there's no separate singular endpoint/method.
    moveToInteractive(request: SubmissionFreeformBulkMoveRequest): Observable<SubmissionFreeformBulkUpdateResponse> {
        return this.http.post<SubmissionFreeformBulkUpdateResponse>(`${this.baseUrl}/bulk-move`, request);
    }

    bulkUpdateIsPublic(submissionIds: string[], isPublic: boolean): Observable<SubmissionFreeformBulkUpdateResponse> {
        return this.http.patch<SubmissionFreeformBulkUpdateResponse>(`${this.baseUrl}/bulk`, {
            submissionIds,
            isPublic
        });
    }

    // Also handles a single submission - the backend's bulk endpoint accepts a one-element
    // submissionIds array just fine, so there's no separate singular endpoint/method.
    deleteSubmissions(submissionIds: string[]): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/bulk`, { body: submissionIds });
    }

    downloadAudioFile(id: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/${id}/audio`, { responseType: "blob" });
    }

    downloadVideoFile(id: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/${id}/video`, { responseType: "blob" });
    }

    replaceAudioFile(id: string, file: File): Observable<void> {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<void>(`${this.baseUrl}/${id}/audio`, formData);
    }

    getAudioFile(id: string): Observable<AudioFile> {
        return this.fetchAudioFile(`${this.baseUrl}/${id}/audio`);
    }

    private fetchAudioFile(endpointUrl: string): Observable<AudioFile> {
        return this.http.get(endpointUrl, { responseType: "blob" }).pipe(
            switchMap(
                blob =>
                    new Observable<AudioFile>(observer => {
                        const url = URL.createObjectURL(blob);
                        const audio = new Audio();
                        audio.addEventListener("loadedmetadata", () => {
                            observer.next({ url, duration: audio.duration, size: blob.size, error: "" });
                            observer.complete();
                        });
                        audio.addEventListener("error", () => {
                            observer.next({
                                url,
                                duration: 0,
                                size: blob.size,
                                error: "Failed to load audio metadata"
                            });
                            observer.complete();
                        });
                        audio.src = url;
                    })
            )
        );
    }
}
